import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import courseService from '../../services/courseService';
import assignmentService from '../../services/assignmentService';
import lectureService from '../../services/lectureService';
import CalendarToolbar from '../../components/CalendarToolbar';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const getTypeLabel = (type) => {
  switch (type) {
    case 'quiz': return '[Quiz]';
    case 'test': return '[Test]';
    case 'homework': return '[HW]';
    case 'essay': return '[Essay]';
    case 'project': return '[Project]';
    case 'discussion': return '[Discussion]';
    case 'lab': return '[Lab]';
    case 'lecture': return '[Lecture]';
    default: return '[Assignment]';
  }
};

function CalendarPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));

  const [view, setView] = useState(isMobile ? 'agenda' : 'month');
  const [date, setDate] = useState(new Date());

  const calendarHeight = isMobile ? 400 : isTablet ? 550 : 700;

  // Color palette for courses
  const courseColors = [
    '#6366f1', // Indigo
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#f59e0b', // Amber
    '#10b981', // Green
    '#3b82f6', // Blue
    '#ef4444', // Red
    '#06b6d4', // Cyan
  ];

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching courses...');
      const coursesResponse = await courseService.getCourses();
      console.log('Courses response:', coursesResponse);

      // Handle different response formats (array or object with results)
      const coursesData = Array.isArray(coursesResponse)
        ? coursesResponse
        : coursesResponse.results || [];

      console.log('Courses fetched:', coursesData.length);
      setCourses(coursesData);

      // Fetch assignments for all courses
      const allEvents = [];
      for (let i = 0; i < coursesData.length; i++) {
        const course = coursesData[i];
        try {
          console.log(`Fetching assignments for course ${course.id}...`);
          const assignmentsData = await assignmentService.getAssignments(course.id);
          const assignments = assignmentsData.results || assignmentsData;
          console.log(`Found ${assignments.length} assignments for ${course.code}`);

          assignments.forEach(assignment => {
            // Add due date event
            if (assignment.due_date) {
              allEvents.push({
                id: assignment.id,
                title: `${getTypeLabel(assignment.type)} ${assignment.title}`,
                start: new Date(assignment.due_date),
                end: new Date(assignment.due_date),
                courseId: course.id,
                courseName: course.name,
                courseCode: course.code,
                color: courseColors[i % courseColors.length],
                type: 'due',
                assignmentType: assignment.type,
                points: assignment.points_possible
              });
            }

            // Add start date event if available
            if (assignment.start_date) {
              allEvents.push({
                id: `start-${assignment.id}`,
                title: `${getTypeLabel(assignment.type)} ${assignment.title} (Opens)`,
                start: new Date(assignment.start_date),
                end: new Date(assignment.start_date),
                courseId: course.id,
                courseName: course.name,
                courseCode: course.code,
                color: courseColors[i % courseColors.length],
                type: 'start',
                assignmentType: assignment.type,
                assignmentId: assignment.id
              });
            }
          });
        } catch (error) {
          console.error(`Error fetching assignments for course ${course.id}:`, error);
        }

        // Fetch lectures for this course
        try {
          const lecturesData = await lectureService.getLectures(course.id);
          const lecturesList = lecturesData.results || lecturesData;
          lecturesList.forEach(lecture => {
            if (lecture.scheduled_at) {
              const start = new Date(lecture.scheduled_at);
              const end = lecture.duration_minutes
                ? new Date(start.getTime() + lecture.duration_minutes * 60000)
                : new Date(start.getTime() + 60 * 60000);
              allEvents.push({
                id: `lecture-${lecture.id}`,
                title: `[Lecture] ${lecture.title}`,
                start,
                end,
                courseId: course.id,
                courseName: course.name,
                courseCode: course.code,
                color: courseColors[i % courseColors.length],
                type: 'lecture',
                assignmentType: 'lecture',
                lectureId: lecture.id,
              });
            }
          });
        } catch (error) {
          console.error(`Error fetching lectures for course ${course.id}:`, error);
        }
      }

      console.log('Total events created:', allEvents.length);
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setError(error.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = selectedCourse === 'all'
    ? events
    : events.filter(event => event.courseId === parseInt(selectedCourse));

  const handleEventClick = (event) => {
    if (event.type === 'lecture') {
      navigate(`/courses/${event.courseId}/lectures`);
      return;
    }
    const assignmentId = event.type === 'start' ? event.assignmentId : event.id;
    navigate(`/courses/${event.courseId}/assignments/${assignmentId}`);
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color,
        borderRadius: '4px',
        opacity: event.type === 'start' ? 0.7 : 1,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.85rem',
        padding: '2px 4px'
      }
    };
  };

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">Loading calendar...</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Paper
            sx={{
              textAlign: 'center',
              py: 8,
              px: 4,
              borderRadius: 3,
              border: 1,
              borderColor: 'error.light',
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" color="error.main" sx={{ mb: 2 }}>
              Error Loading Calendar
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchCalendarData}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                },
              }}
            >
              Try Again
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            Assignment Calendar
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              id="course-select"
              select
              label="Filter by Course"
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
            >
              <MenuItem value="all">All Courses</MenuItem>
              {courses.map((course) => (
                <MenuItem key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {/* Legend */}
        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Legend:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {courses.map((course, index) => (
              <Box
                key={course.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 1,
                    bgcolor: courseColors[index % courseColors.length],
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {course.code} - {course.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Calendar */}
        <Paper
          sx={{
            p: { xs: 1.5, md: 3 },
            mb: 3,
            borderRadius: 3,
            '& .rbc-calendar': {
              fontFamily: "'DM Sans', sans-serif",
            },
            '& .rbc-header': {
              py: 1.5,
              px: 1,
              fontWeight: 600,
              color: 'text.primary',
              borderBottom: 2,
              borderColor: 'divider',
            },
            '& .rbc-today': {
              bgcolor: 'action.hover',
            },
            '& .rbc-off-range-bg': {
              bgcolor: 'background.default',
            },
            '& .rbc-event': {
              cursor: 'pointer',
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              padding: { xs: '4px 6px', sm: '2px 4px' },
            },
            '& .rbc-event:hover': {
              opacity: 0.9,
            },
          }}
        >
          <Calendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: calendarHeight }}
            onSelectEvent={handleEventClick}
            eventPropGetter={eventStyleGetter}
            view={view}
            onView={handleViewChange}
            date={date}
            onNavigate={handleNavigate}
            components={{ toolbar: CalendarToolbar }}
            popup
            tooltipAccessor={(event) => `${event.title} - ${event.courseName}`}
          />
        </Paper>

        <Box aria-live="polite" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          {`Currently viewing ${moment(date).format('MMMM YYYY')}`}
        </Box>

        {/* Upcoming Deadlines */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Upcoming Deadlines
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredEvents
              .filter(event => event.type === 'due' && new Date(event.start) >= new Date())
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .slice(0, 5)
              .map((event, index) => (
                <Box
                  key={`${event.id}-${index}`}
                  onClick={() => handleEventClick(event)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: '12px 16px',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'background.default',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 40,
                      borderRadius: 0.5,
                      bgcolor: event.color,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.courseCode}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: 'primary.main',
                      fontFamily: "'JetBrains Mono', monospace",
                      flexShrink: 0,
                    }}
                  >
                    {moment(event.start).format('MMM DD, YYYY')}
                  </Typography>
                </Box>
              ))}
            {filteredEvents.filter(event => event.type === 'due' && new Date(event.start) >= new Date()).length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                No upcoming deadlines
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default CalendarPage;
